import {
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Truck,
  Package,
  RefreshCcw,
  MapPin,
  type LucideIcon,
} from "lucide-react";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";
import { Badge, type BadgeProps } from "@/components/atoms/badge";

const statusConfig: Record<
  string,
  { variant: BadgeProps["variant"]; icon: LucideIcon }
> = {
  // Order statuses
  waiting_validation: { variant: "warning", icon: Clock },
  validated: { variant: "info", icon: CheckCircle2 },
  in_progress: { variant: "info", icon: RefreshCcw },
  in_delivery: { variant: "secondary", icon: Truck },
  delivered: { variant: "success", icon: Package },
  ready_to_pick_up: { variant: "info", icon: MapPin },
  canceled: { variant: "destructive", icon: XCircle },
  canceled_by_system: { variant: "destructive", icon: XCircle },
  canceled_by_admin: { variant: "destructive", icon: XCircle },
  canceled_by_customer: { variant: "destructive", icon: XCircle },
  canceled_by_seller: { variant: "destructive", icon: XCircle },
  // Legacy/generic statuses
  pending: { variant: "warning", icon: Clock },
  confirmed: { variant: "info", icon: CheckCircle2 },
  processing: { variant: "info", icon: RefreshCcw },
  shipped: { variant: "secondary", icon: Truck },
  cancelled: { variant: "destructive", icon: XCircle },
  refunded: { variant: "outline", icon: RefreshCcw },
  completed: { variant: "success", icon: CheckCircle2 },
  // Product/shop statuses
  active: { variant: "success", icon: CheckCircle2 },
  inactive: { variant: "secondary", icon: XCircle },
  out_of_stock: { variant: "destructive", icon: AlertCircle },
  draft: { variant: "outline", icon: Clock },
};

export interface StatusBadgeProps extends Omit<BadgeProps, "variant"> {
  status: string;
  showIcon?: boolean;
}

function StatusBadge({
  status,
  showIcon = true,
  className,
  children,
  ...props
}: StatusBadgeProps) {
  const { t } = useTranslation();
  const config = statusConfig[status] || {
    variant: "outline" as const,
    icon: AlertCircle,
  };
  const Icon = config.icon;

  // Translate status with fallback to raw value
  const translatedStatus = t(`orders.status.${status}`, { defaultValue: status });

  return (
    <Badge
      variant={config.variant}
      className={cn("gap-1", className)}
      {...props}
    >
      {showIcon && <Icon className="size-3" />}
      {children || translatedStatus}
    </Badge>
  );
}

export { StatusBadge };
